import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import '../styles/Bills.css';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext'; // Import AuthContext

const sanitizeSourceValue = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
};

const Bills = () => {
  const { token, isAuthenticated } = useContext(AuthContext); // Use AuthContext to get token and isAuthenticated

  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editableBill, setEditableBill] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [categories, setCategories] = useState([]);

  // UI density
  const [tableMode, setTableMode] = useState(() => {
    try {
      return localStorage.getItem('billsTableMode') || 'compact';
    } catch {
      return 'compact';
    }
  });
  const [accountsOptions, setAccountsOptions] = useState([]);
  const [creditCards, setCreditCards] = useState([]);

  const [newBill, setNewBill] = useState({
    description: '',
    min_payment: '',
    due_day_of_month: '',
    from_source: '',
    affects_balance: false,
    balance: '',
    comment: '',
    category_id: '',
    active: true,
    fully_paid: false,
    creditcard_id: '',
  });

  // States for filtering
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showFullyPaid, setShowFullyPaid] = useState(false);
  const [showNotFullyPaid, setShowNotFullyPaid] = useState(true);
  const [filterDescription, setFilterDescription] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFromSource, setFilterFromSource] = useState('');

  // NEW: Filter by numeric due day range
  const [startDueDay, setStartDueDay] = useState('');
  const [endDueDay, setEndDueDay] = useState('');

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Add to the state variables at the top of the component
  const [selectedBills, setSelectedBills] = useState([]);

  const fetchBills = useCallback(async () => {
    if (!token || !isAuthenticated) return; // Ensure user is authenticated before fetching
    try {
      const response = await axios.get(`${SERVER_URL}/bills/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (Array.isArray(response.data)) {
        setBills(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
    }
  }, [token, isAuthenticated]);

  const fetchCategories = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/categories/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        console.error('Unexpected categories response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token, isAuthenticated]);

  const fetchAccountOptions = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const resp = await axios.get(`${SERVER_URL}/accounts/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(resp.data)) setAccountsOptions(resp.data);
      else console.error('Unexpected accounts/options format:', resp.data);
    } catch (e) {
      console.error('Failed to load account options', e);
    }
  }, [token, isAuthenticated]);

  const fetchCreditCards = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const resp = await axios.get(`${SERVER_URL}/creditcards/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(resp.data)) setCreditCards(resp.data);
      else console.error('Unexpected creditcards format:', resp.data);
    } catch (e) {
      console.error('Failed to load credit cards', e);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBills();
      fetchCategories();
      fetchAccountOptions();
      fetchCreditCards();
    }
  }, [isAuthenticated, fetchBills, fetchCategories, fetchAccountOptions, fetchCreditCards]);

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const getCreditCardLabel = (creditcardId) => {
    if (!creditcardId) return '—';
    const cc = creditCards.find((c) => c.id === creditcardId);
    if (!cc) return String(creditcardId);
    const last4 = cc.number ? `****${String(cc.number).slice(-4)}` : '';
    return `${cc.name}${last4 ? ' ' + last4 : ''}`;
  };

  const formatSourceShort = (value) => {
    const s = sanitizeSourceValue(value);
    if (!s) return '—';
    // Try to keep last4 if present
    const m = s.match(/\*\*\*\*(\d{4})/);
    const last4 = m ? `****${m[1]}` : '';
    // Take first word(s) as brand
    const brand = s.split(' ')[0] || s;
    return last4 ? `${brand} ${last4}` : brand;
  };

  const setMode = (mode) => {
    setTableMode(mode);
    try {
      localStorage.setItem('billsTableMode', mode);
    } catch {
      // ignore
    }
  };

  const applyFilters = useCallback(() => {
    const filtered = bills.filter((bill) => {
      // 1. Active / Inactive
      const activeMatch =
        (showActive && bill.active) || (showInactive && !bill.active);
      if (!activeMatch) return false;

      // 2. Fully Paid / Not Fully Paid
      const fullyPaidMatch =
        (showFullyPaid && bill.fully_paid) ||
        (showNotFullyPaid && !bill.fully_paid);
      if (!fullyPaidMatch) return false;

      // 3. Description
      const descriptionMatch =
        filterDescription === '' ||
        (bill.description &&
          bill.description.toLowerCase().includes(filterDescription.toLowerCase()));
      if (!descriptionMatch) return false;

      // 4. Category
      const categoryMatch =
        filterCategory === '' || bill.category_id === parseInt(filterCategory, 10);
      if (!categoryMatch) return false;

      // 5. From Source
      const normalizedSource = sanitizeSourceValue(bill.from_source);
      const fromSourceMatch =
        filterFromSource === '' ||
        (normalizedSource &&
          normalizedSource.toLowerCase().includes(filterFromSource.toLowerCase()));
      if (!fromSourceMatch) return false;

      // 6. Due Day Range Filtering
      // If startDueDay or endDueDay are provided, filter accordingly
      const dueDay = parseInt(bill.due_day_of_month, 10);
      if (!isNaN(dueDay)) {
        if (startDueDay !== '' && dueDay < parseInt(startDueDay, 10)) return false;
        if (endDueDay !== '' && dueDay > parseInt(endDueDay, 10)) return false;
      }

      // If all conditions pass, keep this bill
      return true;
    });
    setFilteredBills(filtered);
  }, [
    bills,
    showActive,
    showInactive,
    showFullyPaid,
    showNotFullyPaid,
    filterDescription,
    filterCategory,
    filterFromSource,
    startDueDay,
    endDueDay,
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked, value, type } = e.target;
    if (type === 'checkbox') {
      switch (name) {
        case 'showActive':
          setShowActive(checked);
          break;
        case 'showInactive':
          setShowInactive(checked);
          break;
        case 'showFullyPaid':
          setShowFullyPaid(checked);
          break;
        case 'showNotFullyPaid':
          setShowNotFullyPaid(checked);
          break;
        default:
          break;
      }
    } else {
      // Handle numeric or text inputs
      switch (name) {
        case 'filterDescription':
          setFilterDescription(value);
          break;
        case 'filterCategory':
          setFilterCategory(value);
          break;
        case 'filterFromSource':
          setFilterFromSource(value);
          break;
        case 'startDueDay':
          setStartDueDay(value);
          break;
        case 'endDueDay':
          setEndDueDay(value);
          break;
        default:
          break;
      }
    }
  };

  const openModal = (bill) => {
    setSelectedBill(bill);
    setEditableBill({ ...bill });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedBill(null);
    setEditableBill(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const openFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const hasActiveFilters = useMemo(() => {
    // Defaults: active=true, inactive=false, fullyPaid=false, notFullyPaid=true
    const defaultStatus = showActive === true && showInactive === false;
    const defaultPaid = showFullyPaid === false && showNotFullyPaid === true;

    return (
      !defaultStatus ||
      !defaultPaid ||
      (filterDescription || '').trim() !== '' ||
      (filterCategory || '').trim() !== '' ||
      (filterFromSource || '').trim() !== '' ||
      (startDueDay || '').trim() !== '' ||
      (endDueDay || '').trim() !== ''
    );
  }, [
    showActive,
    showInactive,
    showFullyPaid,
    showNotFullyPaid,
    filterDescription,
    filterCategory,
    filterFromSource,
    startDueDay,
    endDueDay,
  ]);

  const clearFilters = () => {
    setShowActive(true);
    setShowInactive(false);
    setShowFullyPaid(false);
    setShowNotFullyPaid(true);
    setFilterDescription('');
    setFilterCategory('');
    setFilterFromSource('');
    setStartDueDay('');
    setEndDueDay('');
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === 'checkbox') {
      newValue = e.target.checked;
    }

    // IMPORTANT: allow clearing Linked Credit Card (creditcard_id) back to null.
    // HTML <select> returns "" for the empty option; backend expects int|null.
    if (name === 'creditcard_id') {
      if (value === '') newValue = null;
      else newValue = parseInt(value, 10);
    }

    // Keep category_id numeric when present
    if (name === 'category_id') {
      if (value === '') newValue = '';
      else newValue = parseInt(value, 10);
    }

    setEditableBill((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedBill) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/bills/${selectedBill.id}`,
        editableBill,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setBills((prevBills) =>
        prevBills.map((bill) => (bill.id === selectedBill.id ? response.data : bill))
      );
      setUpdateMessage('Bill updated successfully.');
    } catch (error) {
      console.error('Error updating bill:', error);
      setUpdateMessage('Failed to update bill. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedBill) return;
    try {
      // NOTE: backend route is /bills/{id} (no trailing slash). Trailing slash can cause a 307 redirect that breaks DELETE.
      await axios.delete(`${SERVER_URL}/bills/${selectedBill.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBills((prevBills) => prevBills.filter((bill) => bill.id !== selectedBill.id));
      closeModal();
    } catch (error) {
      console.error('Error deleting bill:', error);
      setUpdateMessage('Failed to delete bill. See console for details.');
    }
  };

  const handleCreateBill = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${SERVER_URL}/bills/`, newBill, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setBills([...bills, response.data]);
      setShowCreateModal(false);
      setNewBill({
        description: '',
        min_payment: '',
        due_day_of_month: '',
        from_source: '',
        affects_balance: false,
        balance: '',
        comment: '',
        category_id: '',
        active: true,
        fully_paid: false,
        creditcard_id: '',
      });
    } catch (error) {
      console.error('Error creating bill:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = value;

    if (type === 'checkbox') {
      newValue = e.target.checked;
    }

    // IMPORTANT: allow clearing Linked Credit Card (creditcard_id) back to null.
    // HTML <select> returns "" for the empty option; backend expects int|null.
    if (name === 'creditcard_id') {
      if (value === '') newValue = null;
      else newValue = parseInt(value, 10);
    }

    // Keep category_id numeric when present
    if (name === 'category_id') {
      if (value === '') newValue = '';
      else newValue = parseInt(value, 10);
    }

    setNewBill((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  const copyToPayments = async () => {
    if (!selectedBill) return;
    try {
      await axios.post(
        `${SERVER_URL}/bills/${selectedBill.id}/copy_to_payments/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUpdateMessage('Bill copied to payments successfully.');
    } catch (error) {
      console.error('Error copying bill to payments:', error);
      setUpdateMessage('Failed to copy bill to payments. Please try again.');
    }
  };

  // Add this function to handle checkbox changes
  const handleBillSelection = (billId, checked) => {
    if (checked) {
      setSelectedBills((prev) => [...prev, billId]);
    } else {
      setSelectedBills((prev) => prev.filter((id) => id !== billId));
    }
  };

  // Add this function to handle copying selected bills to payments
  const copySelectedToPayments = async () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to copy.');
      return;
    }

    try {
      const response = await axios.post(
        `${SERVER_URL}/bills/copy_array_to_payments/`,
        { bill_ids: selectedBills },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      alert(`Successfully copied ${selectedBills.length} bills to payments.`);
      setSelectedBills([]); // Clear selection after successful copy
    } catch (error) {
      console.error('Error copying bills to payments:', error);
      alert('Failed to copy bills to payments. Please try again.');
    }
  };

  const totalMinPayment = useMemo(() => {
    return filteredBills.reduce((sum, bill) => sum + parseFloat(bill.min_payment), 0);
  }, [filteredBills]);

  const sourceBreakdown = useMemo(() => {
    const breakdown = {};
    
    filteredBills.forEach((bill) => {
      const source = sanitizeSourceValue(bill.from_source) || 'N/A';
      
      if (!breakdown[source]) {
        breakdown[source] = {
          total: 0,
          fullyPaid: 0,
          processing: 0
        };
      }
      
      const amount = parseFloat(bill.min_payment) || 0;
      breakdown[source].total += amount;
      
      if (bill.fully_paid) {
        breakdown[source].fullyPaid += amount;
      } else {
        breakdown[source].processing += amount;
      }
    });
    
    return breakdown;
  }, [filteredBills]);

  return (
    <div className="bills">
      <Navbar />
      <div className="content">
        <h1>Bills</h1>
        <div className="top-buttons">
          <button className="create-bill-button" onClick={() => setShowCreateModal(true)}>
            Add Bill
          </button>
          <button className="copy-button" onClick={copySelectedToPayments}>
            Copy to Payments
          </button>
          <button
            className="copy-button"
            onClick={() => setMode(tableMode === 'compact' ? 'detailed' : 'compact')}
            title="Toggle table density"
          >
            {tableMode === 'compact' ? 'Detailed view' : 'Compact view'}
          </button>
          <div className="filters-btn-wrap">
            <button className="filter-button" onClick={openFilterModal}>
              Filters
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                className="filter-clear-btn"
                onClick={clearFilters}
                title="Clear filters"
                aria-label="Clear filters"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {isFilterModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeFilterModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Filters</h2>
              <form>
                <label>
                  <input
                    type="checkbox"
                    name="showActive"
                    checked={showActive}
                    onChange={handleFilterChange}
                  />
                  Show Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="showInactive"
                    checked={showInactive}
                    onChange={handleFilterChange}
                  />
                  Show Inactive
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="showFullyPaid"
                    checked={showFullyPaid}
                    onChange={handleFilterChange}
                  />
                  Show Fully Paid
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="showNotFullyPaid"
                    checked={showNotFullyPaid}
                    onChange={handleFilterChange}
                  />
                  Show Not Fully Paid
                </label>
                <label>
                  Description:
                  <input
                    type="text"
                    name="filterDescription"
                    value={filterDescription}
                    onChange={handleFilterChange}
                    placeholder="Enter description"
                  />
                </label>
                <label>
                  Category:
                  <select
                    name="filterCategory"
                    value={filterCategory}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  From Source:
                  <input
                    type="text"
                    name="filterFromSource"
                    value={filterFromSource}
                    onChange={handleFilterChange}
                    placeholder="Enter source"
                  />
                </label>

                {/* NEW: Start / End Due Day fields */}
                <label>
                  Start Due Day:
                  <input
                    type="number"
                    name="startDueDay"
                    value={startDueDay}
                    onChange={handleFilterChange}
                    placeholder="e.g. 5"
                  />
                </label>
                <label>
                  End Due Day:
                  <input
                    type="number"
                    name="endDueDay"
                    value={endDueDay}
                    onChange={handleFilterChange}
                    placeholder="e.g. 10"
                  />
                </label>

                <div className="modal-buttons">
                  <button
                    type="button"
                    className="modal-button close-button"
                    onClick={closeFilterModal}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bills-container">
          <table className="bills-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Due Day</th>

                {tableMode === 'detailed' ? (
                  <>
                    <th>From Source</th>
                    <th>Credit Card Link</th>
                    <th>Affects Balance</th>
                    <th>Balance</th>
                    <th>Comment</th>
                  </>
                ) : (
                  <th>Source</th>
                )}

                <th>Category</th>
                <th>Active</th>

                {tableMode === 'detailed' && <th>Fully Paid</th>}
              </tr>
            </thead>
            <tbody>
              {filteredBills.map((bill, index) => (
                <tr
                  key={bill.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedBills.includes(bill.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleBillSelection(bill.id, e.target.checked);
                      }}
                    />
                  </td>
                  <td onClick={() => openModal(bill)}>{bill.description}</td>
                  <td onClick={() => openModal(bill)}>{bill.min_payment}</td>
                  <td onClick={() => openModal(bill)}>{bill.due_day_of_month}</td>

                  {tableMode === 'detailed' ? (
                    <>
                      <td onClick={() => openModal(bill)}>{bill.from_source}</td>
                      <td onClick={() => openModal(bill)}>{getCreditCardLabel(bill.creditcard_id)}</td>
                      <td onClick={() => openModal(bill)}>{bill.affects_balance ? 'Yes' : 'No'}</td>
                      <td onClick={() => openModal(bill)}>{bill.balance}</td>
                      <td onClick={() => openModal(bill)}>{bill.comment || 'N/A'}</td>
                    </>
                  ) : (
                    <td onClick={() => openModal(bill)}>{formatSourceShort(bill.from_source)}</td>
                  )}

                  <td onClick={() => openModal(bill)}>{getCategoryName(bill.category_id)}</td>
                  <td onClick={() => openModal(bill)}>{bill.active ? 'Yes' : 'No'}</td>

                  {tableMode === 'detailed' && (
                    <td onClick={() => openModal(bill)}>{bill.fully_paid ? 'Yes' : 'No'}</td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td></td>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>Total</strong>
                </td>
                <td style={{ verticalAlign: 'top' }}>
                  <strong>{totalMinPayment.toFixed(2)}</strong>
                </td>
                <td colSpan={tableMode === 'detailed' ? 9 : 4} style={{ verticalAlign: 'top' }}>
                  <div style={{ textAlign: 'left', fontSize: '0.9em' }}>
                    {Object.keys(sourceBreakdown).length > 0 ? (
                      Object.entries(sourceBreakdown).map(([source, amounts]) => (
                        <div key={source} style={{ marginBottom: '4px' }}>
                          <strong>{source}:</strong>
                          <div style={{ paddingLeft: '10px', fontSize: '0.85em' }}>
                            Total: ${amounts.total.toFixed(2)}<br />
                            Fully Paid: ${amounts.fullyPaid.toFixed(2)}<br />
                            Processing: ${amounts.processing.toFixed(2)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span>No data</span>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="bills-tiles">
            {filteredBills.map((bill) => (
              <div
                key={bill.id}
                className="bill-tile"
                onClick={() => openModal(bill)}
              >
                <div className="bill-description">{bill.description}</div>
                <div className="bill-amount">
                  <label>Amount: </label>
                  {bill.min_payment}
                </div>
                <div className="bill-due-day">
                  <label>Due Day: </label>
                  {bill.due_day_of_month}
                </div>
                <div className="bill-from-source">
                  <label>From Source: </label>
                  {bill.from_source}
                </div>
                <div className="bill-balance">
                  <label>Balance: </label>
                  {bill.balance}
                </div>
                <div className="bill-comment">
                  <label>Comment: </label>
                  {bill.comment || 'N/A'}
                </div>
                <div className="bill-category">
                  <label>Category: </label>
                  {getCategoryName(bill.category_id)}
                </div>
                <div className="bill-active">
                  <label>Active: </label>
                  {bill.active ? 'Yes' : 'No'}
                </div>
                <div className="bill-fully-paid">
                  <label>Fully Paid: </label>
                  {bill.fully_paid ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isModalOpen && editableBill && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Bill</h2>
              <form>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={editableBill.description}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Minimum Payment:
                  <input
                    type="number"
                    name="min_payment"
                    value={editableBill.min_payment}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Due Day of Month:
                  <input
                    type="number"
                    name="due_day_of_month"
                    value={editableBill.due_day_of_month}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  From Source:
                  <select
                    name="from_source"
                    value={editableBill.from_source || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Source</option>
                    {accountsOptions.map((opt) => {
                      const optionValue = sanitizeSourceValue(opt.value ?? opt.label ?? '');
                      const optionLabel = sanitizeSourceValue(opt.label ?? opt.value ?? '');
                      return (
                        <option key={opt.id} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  Linked Credit Card:
                  <select
                    name="creditcard_id"
                    value={editableBill.creditcard_id ?? ''}
                    onChange={handleInputChange}
                  >
                    <option value="">— none —</option>
                    {creditCards.map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.name} {cc.number ? `****${String(cc.number).slice(-4)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-checkbox">
                  Affects Balance:
                  <input
                    type="checkbox"
                    name="affects_balance"
                    checked={!!editableBill.affects_balance}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={editableBill.balance}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Comment:
                  <input
                    type="text"
                    name="comment"
                    value={editableBill.comment || ''}
                    onChange={handleInputChange}
                    placeholder="Enter comment"
                  />
                </label>
                <label>
                  Category:
                  <select
                    name="category_id"
                    value={editableBill.category_id || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableBill.active}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Fully Paid:
                  <input
                    type="checkbox"
                    name="fully_paid"
                    checked={editableBill.fully_paid}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div
                  className={`modal-message ${
                    updateMessage.includes('success') ? 'success' : 'error'
                  }`}
                >
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button copy-button" onClick={copyToPayments}>
                  Copy to Payments
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Bill</h2>
              <form onSubmit={handleCreateBill}>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={newBill.description}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Minimum Payment:
                  <input
                    type="number"
                    name="min_payment"
                    value={newBill.min_payment}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Due Day of Month:
                  <input
                    type="number"
                    name="due_day_of_month"
                    value={newBill.due_day_of_month}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  From Source:
                  <select
                    name="from_source"
                    value={newBill.from_source || ''}
                    onChange={handleNewInputChange}
                  >
                    <option value="">Select Source</option>
                    {accountsOptions.map((opt) => {
                      const optionValue = sanitizeSourceValue(opt.value ?? opt.label ?? '');
                      const optionLabel = sanitizeSourceValue(opt.label ?? opt.value ?? '');
                      return (
                        <option key={opt.id} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  Linked Credit Card:
                  <select
                    name="creditcard_id"
                    value={newBill.creditcard_id || ''}
                    onChange={handleNewInputChange}
                  >
                    <option value="">— none —</option>
                    {creditCards.map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.name} {cc.number ? `****${String(cc.number).slice(-4)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-checkbox">
                  Affects Balance:
                  <input
                    type="checkbox"
                    name="affects_balance"
                    checked={!!newBill.affects_balance}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={newBill.balance}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Comment:
                  <input
                    type="text"
                    name="comment"
                    value={newBill.comment}
                    onChange={handleNewInputChange}
                    placeholder="Enter comment"
                  />
                </label>
                <label>
                  Category:
                  <select
                    name="category_id"
                    value={newBill.category_id || ''}
                    onChange={handleNewInputChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newBill.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Fully Paid:
                  <input
                    type="checkbox"
                    name="fully_paid"
                    checked={newBill.fully_paid}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bills;
